<template>
	<div
		ref="timeline"
		class="timeline"
		:class="{
			[mode]: true,
			'is-live': isLive,
			'is-vod': !isLive,
			'is-desktop': isDesktopWebApp,
			'is-marking': markingActive,
			'is-arena': !isTaggingTool,
			'is-full-page': isFullPageMode,
			'is-edit-mode': isEditMode,
			'is-tagging-tool': isTaggingTool,
			'is-zoom-changing': zoomIsChanging,
			'has-couch-mode-button': showCouchModeButton,
			'show-participants': showParticipants,
		}"
	>
		<div
			ref="wrapper"
			class="timeline__inner-w"
		>
			<!-- top left empty corner -->
			<div class="timeline__top-left-corner" />
			<!-- tagging tool ruler 0 | 60 | 180 | etc -->
			<Ruler
				v-if="isTaggingTool"
				ref="ruler"
				class="timeline__ruler"
				:is-mobile-web-app="isMobileWebApp"
				:duration="duration"
				:zoom-start="zoomStart"
				:zoom-level="zoomLevel"
				@seek="handlePercentageSeek"
			/>
			<!-- participants -->
			<div
				v-if="showParticipants"
				class="timeline__participants"
			>
				<template
					v-for="(participant, index) in participantItems"
				>
					<div
						v-if="index === 1"
						:key="`participant-filler-${participant.id}`"
						class="timeline__participant -multi-spacer"
					/>
					<div
						:key="`participant-${participant.id}`"
						class="timeline__participant"
					>
						<img
							v-tooltip.right="{
								content: participantTooltip(participant),
								classes: ['white-theme', 'font-sm'],
								container: $root.$el,
							}"
							class="timeline__participant-image"
							:src="participantImage(participant)"
						>
					</div>
				</template>
			</div>
			<!-- main area -->
			<div
				ref="main"
				class="timeline__main-time-axis"
				@mousemove="handleMainMouseMove($event)"
			>
				<div class="timeline__main-time-axis-clipper -timebox">
					<!-- timebox -->
					<div
						v-if="hasTimebox"
						class="timeline__timebox"
						:style="timeboxStyle"
					>
						{{ timeboxLabel }}
					</div>
				</div>
				<div class="timeline__main-time-axis-clipper -main">
					<div
						ref="clickWrapper"
						class="timeline__click-w"
						:style="clickWrapperStyle"
						@click.stop="handleClick"
						@mousedown="handleMouseDown"
						@touchstart="handleMouseDown"
					>
						<!-- needle -->
						<div
							v-if="isTaggingTool"
							ref="needle"
							class="timeline__needle"
							:class="{
								'-invisible': markingActive,
								'is-live': isLive,
							}"
							:style="needleStyle"
						/>
						<!-- seek bar -->
						<div
							v-if="!isTaggingTool"
							class="timeline__seek-bar"
						/>
						<!-- progress bar -->
						<div
							v-if="!isTaggingTool"
							ref="strike"
							class="timeline__progress-bar"
							:style="highlightStyle"
						/>
						<!-- strike -->
						<div
							v-if="!isEditMode && isLive"
							class="timeline__needle-to-live-strike"
							:style="{width: needleToLiveWidth}"
						/>
						<!-- tag rows -->
						<div
							v-if="isTaggingTool && !isEditMode"
							class="timeline__tag-bg-rows"
							:class="{
								'is-duell': isDuell,
								'is-live': isLive,
							}"
							:style="{
								'--live-progress-width': isLive ? needleToLiveWidth : '0%',
							}"
						>
							<div
								v-if="isDuell"
								class="timeline__tag-bg-row"
							/>
							<div class="timeline__tag-bg-row" />
							<div
								v-if="isDuell"
								class="timeline__tag-bg-row"
							/>
						</div>
						<!-- mark in/out zone -->
						<MarkingZone
							:event-duration="duration"
							:zoom-level="zoomLevel"
							:elapsed="elapsed"
						/>
						<template v-if="!isEditMode">
							<!-- tags -->
							<div
								v-for="tag in timelineSingleItems"
								:key="tag.key"
								class="timeline__tag js-tag"
								:class="[{
									'is-active': singlePreviewTag && singlePreviewTag.id === tag.id,
									'-neutral': isTaggingTool && tag.isNeutral && (!isHighlightsMode || tag.scoreIntensity === 0),
									'-light-mode': $arenaConfig.isLightTheme && !isFullscreen && !isCinemaMode,
								}, '-score-' + tag.scoreIntensity ]"
								:style="tag.style"
								@mouseenter.stop="handleTagHover($event, tag)"
								@mouseleave.stop="handleTagLeave($event, tag)"
								@touchend.stop="handleTagHover($event, tag)"
								@click="handleTagClick($event, tag)"
							/>
							<!-- pills -->
							<Pill
								v-for="group in timelineMultiItems"
								:key="group.key"
								class="timeline__tag -pill js-pill"
								:class="{
									'is-active': multiPreviewGroup && multiPreviewGroup.id === group.id,
									'-light-mode': $arenaConfig.isLightTheme && !isFullscreen && !isCinemaMode,
								}"
								:hovered="multiPreviewGroup && multiPreviewGroup.id === group.id"
								:style="group.style"
								:group="group"
								:light-mode="$arenaConfig.isLightTheme && !isFullscreen && !isCinemaMode"
								@enter="handlePillEnter"
								@leave="handlePillLeave"
								@click="handlePillClick($event, group)"
							/>
						</template>
						<!-- edit bar -->
						<EditBar
							v-if="isEditMode"
							ref="editBar"
							:event-duration="duration"
							:tag="editedTag"
							:zoom-level="zoomLevel"
							:is-paused="isPaused"
							:elapsed="elapsed"
							:is-mobile-web-app="isMobileWebApp"
							:is-mobile-web-app-portrait="isMobileWebAppPortrait"
							:is-mobile-web-app-landscape="isMobileWebAppLandscape"
							@marking-update="handleMarkingUpdate"
							@pause="$emit('pause')"
						/>
					</div>
				</div>
			</div>

			<!-- view-mode right side -->
			<!-- COUCH MODE BUTTON -->
			<div
				v-if="showCouchModeButton"
				class="timeline__after"
			>
				<button
					v-tooltip="{
						content: $I18n.trans('view_mode.couch_mode'),
						container: $root.$el,
					}"
					class="timeline__couch-button"
					:class="{
						'-light-mode': $arenaConfig.isLightTheme && !isFullscreen && !isCinemaMode,
					}"
					@click.stop="handleCouch"
				>
					<Icon
						icon-name="sofa"
						size="xl"
					/>
				</button>
			</div>
		</div>

		<!-- ZoomBar -->
		<ZoomBar
			v-if="showZoomBar"
			:start-offset="zoomStart"
			:zoom-level="zoomLevel"
			:duration="duration"
			:tags="timelineItems"
			:edited-tag="editedTag"
			@change-start="handleZoomStartChange"
			@change-level="handleZoomLevelChange"
			@is-changing="zoomIsChanging = $event"
		/>

		<!-- Tag preview -->
		<PreviewContainer
			v-if="isPreviewing"
			ref="preview"
			:position="previewPosition"
			@mouseenter="handlePreviewEnter"
			@mouseleave="handlePreviewLeave"
		>
			<!-- single -->
			<TagPreview
				v-if="isSinglePreview"
				:id="singlePreviewTag.id"
				:name="computePreviewTagName(singlePreviewTag)"
				:time="singlePreviewTag.time"
				:game-minute="singlePreviewTag.tag.gameTime.gameMinute"
				:no-edit="!isTaggingTool"
				:seconds-before="singlePreviewTag.tag.secondsBefore"
				:seconds-after="singlePreviewTag.tag.secondsAfter"
				:sport-name="sportName"
				:is-playing="playedPreviewTag && playedPreviewTag.id === singlePreviewTag.id"
				@play="handlePreviewPlay"
				@edit="handlePreviewEdit"
				@share="handlePreviewShare"
				@pause="handlePreviewPause"
				@remove="handlePreviewRemove"
			/>
			<!-- multi -->
			<div
				v-if="isMultiPreview"
				class="timeline__multi-preview"
			>
				<TagPreview
					v-for="item in multiPreviewTags"
					:id="item.id"
					:key="`multi-preview-${item.id}`"
					:name="computePreviewTagName(item)"
					:time="item.time"
					:game-minute="item.tag.gameTime.gameMinute"
					:no-edit="!isTaggingTool"
					:is-in-multi="true"
					:seconds-before="item.tag.secondsBefore"
					:seconds-after="item.tag.secondsAfter"
					:sport-name="sportName"
					:is-playing="playedPreviewTag && playedPreviewTag.id === item.id"
					@play="handlePreviewPlay"
					@edit="handlePreviewEdit"
					@share="handlePreviewShare"
					@pause="handlePreviewPause"
					@remove="handlePreviewRemove"
				/>
			</div>
		</PreviewContainer>
	</div>
</template>

<script>
import Icon from '@components/Icon';
import Ruler from './Ruler.vue';
import PreviewContainer from './PreviewContainer.vue';
import TagPreview from '../TagPreview';
import Pill from './Pill.vue';
import EditBar from './EditBar.vue';
import MarkingZone from '@components/timeline/MarkingZone';
import ZoomBar from '@components/timeline/ZoomBar';
import { mapActions, mapGetters } from 'vuex';
import { DOT_SIZE, DOT_SIZE_HOVERED, MODE, PILL_MERGE_DELTA, DOT_HOVERED_WIDTH_ADDITION } from './constants';
import { PARENT_HIGHLIGHT } from '../../constants/categories';
import { computeHighlightPercentage, computeTags } from './timeline-logic';
import { getTimeLabelFromSeconds } from '../../util/helpers';
import { isSingleSport } from '@shared/utils/eventUtils';
import { EVENT, bus, playerBus } from '@videotools/js/events';
import { TAG_NAME_RUN } from '@videotools/js/api/entities/TagButton';

const MARK_ZOOM_UPGRADE_TRIGGER_PERCENTAGE = 80;
const MARK_ZOOM_UPGRADE_RATIO = 1.5;
const MARK_ZOOM_OFFSET_PERCENTAGE = 10;

const nullParticipant = {
	id:   -1,
	name: '',
};

const events = [EVENT.TIMELINE_HIGHLIGHT_TAGS, EVENT.TIMELINE_HIGHLIGHT_CLEAR, EVENT.PLAYER.ON.PLAY, EVENT.TIMELINE_EDIT_LATEST_TAG];

export default {
	name:       'Timeline',
	components: {
		ZoomBar,
		MarkingZone,
		Ruler,
		PreviewContainer,
		TagPreview,
		Pill,
		Icon,
		EditBar,
	},
	props: {
		event: {
			type:     Object,
			required: true,
		},
		mode: {
			type:    String,
			default: MODE.DEFAULT,
		},
		duration: {
			type:    Number,
			default: 0,
		},
		elapsed: {
			type:    Number,
			default: 0,
		},
		participants: {
			type:    Array,
			default: () => [],
		},
		tags: {
			type:    Array,
			default: () => [],
		},
		editedTag: {
			type:    Object,
			default: null,
		},
		isPaused: {
			type: Boolean,
		},
		isTaggingTool: {
			type:    Boolean,
			default: false,
		},
		showZoomBar: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			isMounted:              false,
			zoomStart:              0,
			zoomLevel:              1,
			zoomIsChanging:         false,
			savedZoomStart:         0,
			savedZoomLevel:         1,
			savedLocation:          null,
			wasPlayingBeforeSeek:   false,
			absoluteNeedlePosition: 0,
			previewEvent:           null,
			singlePreviewTag:       null,
			multiPreviewTags:       [],
			multiPreviewGroup:      null,
			previewTriggerTarget:   null,
			contextPlayer:          null,
			lastMainMoveEvent:      null,
			playedPreviewTag:       null,
			highlightedTags:        [],
			editBarRef:             null,

			playerIsAdMode:      false,
			adPosition:          0,
			adDuration:          0,
			isCursorDragging:    false,
			cursorDraggingTimer: null,
			resizeObserver:      null,
		};
	},
	computed: {
		...mapGetters({
			isCinemaMode:            'app/isCinemaMode',
			isDesktopWebApp:         'app/isDesktopWebApp',
			isFullPageMode:          'app/isFullPageMode',
			isMobileWebApp:          'app/isMobileWebApp',
			isMobileWebAppPortrait:  'app/isMobileWebAppPortrait',
			isMobileWebAppLandscape: 'app/isMobileWebAppLandscape',
			isMobileViewport:        'device/isMobileViewport',
			isLandscape:             'device/isLandscape',
			isTouchDevice:           'device/isTouchDevice',
			isFullscreen:            'device/fullscreen',
			sportName:               'event/sportName',
			sportParticipantType:    'event/participantType',
			couchTags:               'event/couchTags',
			streamLeadTimeSeconds:   'event/streamLeadTimeSeconds',
			_isLive:                 'event/isLive',
			isSequentialCompetition: 'event/isSequentialCompetition',
			streamUrl:               'event/streamURL',
			autostartCouchMode:      'features/autostartCouchMode',
			markingActive:           'markingZone/active',
			markingStart:            'markingZone/start',
			isFakeDVR:               'player/isFakeDVR',
			isHighlightsMode:        'highlights/isHighlightsMode',
		}),
		isLive() {
			return this._isLive && !this.isFakeDVR;
		},
		isHighlightCateogry() {
			return this.event.categoryParent === PARENT_HIGHLIGHT;
		},
		isEditMode() {
			return this.mode === MODE.EDIT;
		},
		showCouchModeButton() {
			return !this.isTaggingTool && !this.isHighlightCateogry && this.hasCouchTags && this.couchmodeAvailable;
		},
		timelineItems() {
			if (!this.isMounted) return [];
			if (!this.duration) return [];

			// TODO rework this so that we don't recompute the whole timeline tag list when a tag is hovered
			const singleTagHovered = (tagId) => this.singlePreviewTag && this.singlePreviewTag.id === tagId;

			let computedTags = computeTags(
				this.tags.map((t) => ({
					...t.tag,
					participant: t.participant,
					hovered:     singleTagHovered(t.id),
				})),
				{
					participants:            this.participants,
					duration:                this.duration,
					mode:                    this.mode,
					isMobileViewport:        this.isMobileViewport,
					isMobile:                this.isMobileWebApp,
					isLandscape:             this.isLandscape,
					isSequentialCompetition: this.isSequentialCompetition,
					highlightedTags:         this.highlightedTags,
					isSingleSport:           isSingleSport(this.event),
				}
			);

			if (this.isSequentialCompetition && this.contextPlayer) {
				computedTags = computedTags.filter((tag) => (tag.participant && tag.participant.id === this.contextPlayer.id) || tag.participant === null);
			}

			if (!this.$refs.clickWrapper) return computedTags;

			const positionedTags = [];
			const containerWidth = this.$refs.clickWrapper.offsetWidth;

			for (const tag of computedTags) {
				const originalLeft = tag.style.left;
				const dotLeftRatio = tag.time / this.duration;
				const left = containerWidth * dotLeftRatio - DOT_SIZE / 2;
				const right = left + DOT_SIZE;
				const width = containerWidth * ((tag.tag.secondsBefore + tag.tag.secondsAfter) / this.duration);
				positionedTags.push({
					left,
					originalLeft,
					right,
					tag,
					time:     tag.time,
					key:      tag.key,
					width,
					original: tag,
				});
			}
			positionedTags.sort((a, b) => (a.participantIndex === b.participantIndex ? a.left - b.left : a.participantIndex - b.participantIndex));

			// build groups
			const leveledTags = (() => {
				return positionedTags.reduce((acc, curr) => {
					if (acc[curr.original.participantIndex]) {
						acc[curr.original.participantIndex].push(curr);
					} else {
						acc[curr.original.participantIndex] = [curr];
					}
					return acc;
				}, {});
			})();

			const groupTags = (_tags) => {
				if (_tags.length === 0) {
					return _tags;
				}
				const groups = [[_tags[0]]];
				for (const tag of _tags.slice(1)) {
					const lastGroupedTag = groups[groups.length - 1][groups[groups.length - 1].length - 1];
					if (tag.left <= lastGroupedTag.right + PILL_MERGE_DELTA && (!tag.tag.score || tag.tag.score < 3)) {
						groups[groups.length - 1].push(tag);
					} else {
						groups.push([tag]);
					}
				}
				return groups;
			};

			const groupedTags = this.isHighlightsMode ? Object.values(leveledTags) : Object.values(leveledTags).map(groupTags);

			// flatten
			const flat = groupedTags.flat().reduce((acc, curr) => {
				if (Array.isArray(curr) && curr.length === 1) {
					acc.push(curr[0]);
				} else {
					acc.push(curr);
				}
				return acc;
			}, []);

			const multiTagHovered = (groupId) => this.multiPreviewGroup && this.multiPreviewGroup.id === groupId;

			return flat.map((e) => {
				if (Array.isArray(e)) {
					const ids = e.reduce((acc, curr) => acc.concat(curr.tag.id), []);
					const key = `tag-group-${ids.reduce((acc, curr) => `${acc}-${curr}`, '')}`;

					let first = e[0];
					let last = e[0];
					for (const el of e.slice(1)) {
						if (el.left < first.left) first = el;
						if (el.left + el.width > last.left + last.width) last = el;
					}
					const { left } = first;
					const width = Math.max(DOT_SIZE, ((last.time - first.time) * containerWidth) / this.duration);

					const groupHovered = multiTagHovered(ids.join('-'));
					return {
						isGroup: true,
						key,
						id:      ids.join('-'),
						ids,
						tags:    e,
						style:   {
							top:  `calc(${first.tag.topPosition}% - ${groupHovered ? DOT_SIZE_HOVERED : DOT_SIZE}px)`,
							left: `${groupHovered ? first.left - DOT_HOVERED_WIDTH_ADDITION / 4 : first.left}px`,
						},
						left,
						width,
						time: first.time,
					};
				}
				return e.original;
			});
		},
		hasTags() {
			return this.timelineItems.length > 0;
		},
		hasCouchTags() {
			return this.couchTags.length > 0;
		},
		couchmodeAvailable() {
			return this.event?.couchmodeAvailable || false;
		},
		participantItems() {
			let contextParticipants = this.participants;

			if (this.isSequentialCompetition) {
				contextParticipants = [this.contextPlayer || nullParticipant];
			}

			if (this.isEditMode) {
				if (!this.editedTag) return contextParticipants;
				const tag = this.tags.find((t) => t.id === this.editedTag.id);
				if (!tag) return contextParticipants;
				const filtered = contextParticipants.filter((participant) => participant.id === tag.participant);
				if (filtered.length === 0) return contextParticipants;
				return filtered;
			}

			return contextParticipants;
		},
		timelineMultiItems() {
			return this.timelineItems.filter((e) => e.isGroup);
		},
		timelineSingleItems() {
			return this.timelineItems.filter((e) => !e.isGroup);
		},
		timeboxLabel() {
			const time = Number.isFinite(this.elapsed) ? this.elapsed : 0;

			return getTimeLabelFromSeconds(time);
		},
		highlightStyle() {
			// full highlight on tagging tool - vod
			if (this.isTaggingTool && !this.isLive) return { width: '100%' };

			const width = computeHighlightPercentage({
				elapsed:  this.isLive && this.isTaggingTool ? playerBus.mediaDuration - this.streamLeadTimeSeconds : this.elapsed,
				duration: this.duration,
			});

			return {
				width: `${width}%`,
			};
		},
		needleToLiveWidth() {
			if (!this.isLive) return { width: 0 };

			const width = computeHighlightPercentage({
				elapsed:  playerBus.mediaDuration - this.streamLeadTimeSeconds,
				duration: this.duration,
			});

			return `${width}%`;
		},
		clickWrapperStyle() {
			if (!this.duration) {
				return {
					width: '100%',
				};
			}

			const width = Math.max(100, this.zoomLevel * 100);

			let left = (-1 * (this.zoomStart * 100)) / this.duration;
			if (!Number.isFinite(left)) left = 0;

			return {
				width:     `${width}%`,
				transform: `translateX(${left}%)`,
			};
		},
		needleStyle() {
			if (!this.isMounted) return {};

			const left = (this.elapsed / this.duration) * 100 || 0;

			return {
				left: `${left}%`,
			};
		},
		timeboxStyle() {
			const left = this.absoluteNeedlePosition;

			return {
				transform: `translate(calc(${left}px - 50%), 0)`,
			};
		},
		previewPosition() {
			if (!this.isMounted) return null;
			if (!this.previewEvent || !this.$refs.timeline) return null;
			const targetRect = this.previewEvent.target.getBoundingClientRect();
			const targetCenter = {
				x: targetRect.x + targetRect.width / 2,
				y: targetRect.y + targetRect.height / 2,
			};
			const parentRect = this.$refs.timeline.getBoundingClientRect();

			const position = {
				target: targetRect,
				parent: parentRect,
				targetCenter,
			};

			position.bottom = parentRect.bottom - targetCenter.y;

			return position;
		},
		isSinglePreview() {
			return this.singlePreviewTag !== null;
		},
		isMultiPreview() {
			return Array.isArray(this.multiPreviewTags) && this.multiPreviewTags.length > 0;
		},
		isPreviewing() {
			return this.isSinglePreview || this.isMultiPreview;
		},
		showFakeParticipant() {
			const everyParticipantHasImage = this.participants.every((participant) => this.participantImage(participant));
			return (this.participants.length > 2 || this.participants.length === 0) || !everyParticipantHasImage;
		},
		showParticipants() {
			const everyParticipantHasImage = this.participants.every((participant) => this.participantImage(participant));
			return this.participants.length === 2 && everyParticipantHasImage;
		},
		isDuell() {
			return this.participants.length === 2;
		},
		hasTimebox() {
			return this.isTaggingTool && !this.isEditMode;
		},
	},
	watch: {
		mode(val) {
			console.log('mode', val);
			// when entering edit mode, zoom to fixed duration
			if (val === MODE.EDIT) {
				// backup zoom
				this.savedZoomStart = this.zoomStart;
				this.savedZoomLevel = this.zoomLevel;
				this.savedLocation = this.elapsed;
			} else {
				// restore zoom
				this.zoomLevel = this.savedZoomLevel;
				this.zoomStart = this.savedZoomStart;
				playerBus.seek(this.savedLocation);
			}
		},
		needleStyle() {
			this.updateNeedlePosition();
		},
		zoomLevel() {
			this.forceRerender();
		},
		zoomStart() {
			this.updateNeedlePosition();
		},
		duration() {
			this.updateNeedlePosition();
		},
		isPreviewing(value) {
			if (value) {
				bus.publish(EVENT.TIMELINE_TAG_PREVIEW_OPEN);
			} else {
				bus.publish(EVENT.TIMELINE_TAG_PREVIEW_CLOSE);
			}
		},
		'editedTag.id'() {
			this.adaptToEditTagMode();
		},
	},
	mounted() {
		this.isMounted = true;
		this.updateNeedlePosition();
		bus.subscribe(EVENT.PLAYER.ON.PAUSE, this.handlePlayerPause);
		bus.subscribe(EVENT.TAG_PLAY, this.handleTagPlay);

		bus.subscribe(EVENT.PLAYER.ON.AD_MODE_ENTER, this.handleAdModeEnter);
		bus.subscribe(EVENT.PLAYER.ON.AD_MODE_EXIT, this.handleAdModeExit);
		bus.subscribe(EVENT.PLAYER.ON.AD_TIME, this.handleAdTime);

		this.contextPlayer = null;
		bus.subscribe(EVENT.TAG_BUTTONS_PARTICIPANT_SELECT, this.handleTagButtonsParticipantSelect);

		// subscribe to external events
		for (const event of events) {
			bus.subscribe(event, this[`BUS-${event}`]);
		}
		this.$set(this.$data, 'editBarRef', this.$refs.editBar);

		if (this.$refs.timeline && window.ResizeObserver) {
			this.resizeObserver = new ResizeObserver(() => {
				this.forceRerender();
			});
			this.resizeObserver.observe(this.$refs.timeline);
		}
	},
	destroy() {
		bus.unsubscribe(EVENT.PLAYER.ON.PAUSE, this.handlePlayerPause);
		bus.unsubscribe(EVENT.TAG_PLAY, this.handleTagPlay);

		bus.unsubscribe(EVENT.PLAYER.ON.AD_MODE_ENTER, this.handleAdModeEnter);
		bus.unsubscribe(EVENT.PLAYER.ON.AD_MODE_EXIT, this.handleAdModeExit);
		bus.unsubscribe(EVENT.PLAYER.ON.AD_TIME, this.handleAdTime);

		bus.unsubscribe(EVENT.TAG_BUTTONS_PARTICIPANT_SELECT, this.handleTagButtonsParticipantSelect);

		// unsubscribe from external events
		for (const event of events) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}

		if (this.$refs.timeline && this.resizeObserver) this.resizeObserver.unobserve(this.$refs.timeline);
	},
	methods: {
		...mapActions({
			enableCouchMode:           'app/enableCouchMode',
			deleteEventTags:           'event/deleteEventTags',
			disableAutostartCouchMode: 'features/disableAutostartCouchMode',
			editEventTag:              'eventTagEdit/editEventTag',
		}),
		[`BUS-${EVENT.TIMELINE_HIGHLIGHT_TAGS}`](tags) {
			this.highlightedTags = tags;
		},
		[`BUS-${EVENT.TIMELINE_HIGHLIGHT_CLEAR}`]() {
			this.highlightedTags = [];
		},
		[`BUS-${EVENT.PLAYER.ON.PLAY}`]() {
			const elapsed = Math.floor(this.elapsed);
			const zoomStart = Math.floor(this.zoomStart);
			const visibleDuration = this.duration / this.zoomLevel;

			if (this.isTaggingTool && (elapsed < this.zoomStart || elapsed > this.zoomStart + visibleDuration)) {
				if (this.playedPreviewTag) {
					this.zoomStart = Math.min(this.playedPreviewTag.timestamp - (this.playedPreviewTag.secondsBefore || 0), this.duration - visibleDuration);
				} else {
					playerBus.seek(zoomStart);
				}
			}
		},
		[`BUS-${EVENT.TIMELINE_EDIT_LATEST_TAG}`]() {
			if (this.tags.length === 0) return;
			const sortedTags = [...this.tags];
			sortedTags.sort((a, b) => (a.timestamp - a.secondsBefore < b.timestamp - b.secondsBefore ? -1 : 1));
			const latestTag = sortedTags[sortedTags.length - 1];
			this.editEventTag(latestTag);
		},
		participantImage(participant) {
			return participant?.original?.participatable?.imageUrl;
		},
		handlePreviewPause() {
			if (!playerBus.isPlaying) return;
			playerBus.pause();
			this.handlePlayerPause();
		},
		translateTagName(name, tagType) {
			return this.transTagLabel(name, this.sportName, tagType);
		},
		updateNeedlePosition() {
			if (!this.hasTimebox) return;
			this.$nextTick(() => {
				this.$raf('timeline-force-render', () => {
					const rulerRef = this.$refs.ruler?.$el;
					const needleRef = this.$refs.needle;
					if (!rulerRef || !needleRef) return;
					const rulerRect = rulerRef.getBoundingClientRect();
					const needleRect = needleRef.getBoundingClientRect();
					this.absoluteNeedlePosition = needleRect.x - rulerRect.x;
				});
			});
		},
		participantTooltip(participant) {
			const original = participant?.original;
			if (original?.context === 'player') return original?.participatable?.name;
			return original?.participatable?.club?.name;
		},
		handleTagButtonsParticipantSelect(id) {
			if (!this.isSequentialCompetition) return;
			this.contextPlayer = this.participants.find((e) => e.id === id) || null;
		},
		handleZoomStartChange(value) {
			this.zoomStart = value;
		},
		handleZoomLevelChange(value) {
			this.zoomLevel = Math.max(1, value);
		},
		getXRatio(clientX, containerX, containerWidth, asPercentage = true) {
			let x = clientX - containerX;
			// offset X
			if (x < 0) {
				x = 0;
			}
			const val = x / containerWidth;
			return asPercentage ? val * 100 : val;
		},
		handleClick(event) {
			const ref = this.$refs.clickWrapper || this.$refs.main;
			if (!ref || this.isEditMode) return;
			if (['js-tag', 'js-pill'].some((className) => event.target.closest(`.${className}`))) {
				return;
			}
			const sizes = event.touches ? [event.touches[0].clientX, event.touches[0].clientY] : [event.clientX, event.clientY];
			const [x, _y] = sizes;
			const rect = ref.getBoundingClientRect();
			const width = ref.offsetWidth;

			this.handlePercentageSeek(this.getXRatio(x, rect.x, width, true));
		},
		handleMouseDown(e) {
			this.handleCursorDragStart();
			// Add safety checks to prevent errors when playerBus isn't properly initialized
			this.wasPlayingBeforeSeek = playerBus && playerBus.player ? playerBus.isPlaying : false;
			if (playerBus && playerBus.player && playerBus.isPlaying) {
				playerBus.pause();
			}
			document.addEventListener('mousemove', this.handleMouseMove);
			document.addEventListener('mouseup', this.handleMouseUp);
			document.addEventListener('touchmove', this.handleMouseMove);
			document.addEventListener('touchend', this.handleMouseUp);
			this.handleClick(e);
		},
		handleMouseMove(e) {
			this.handleClick(e);
		},
		handleMouseUp() {
			this.handleCursorDragEnd();
			const ref = this.$refs.clickWrapper || this.$refs.main;
			if (!ref) return;
			if (this.wasPlayingBeforeSeek && playerBus && playerBus.player) {
				playerBus.play();
			}
			document.removeEventListener('mousemove', this.handleMouseMove);
			document.removeEventListener('mouseup', this.handleMouseUp);
			document.removeEventListener('touchmove', this.handleMouseMove);
			document.removeEventListener('touchend', this.handleMouseUp);
		},
		percentageToTime(percentage) {
			const clampedValue = Math.min(100, Math.max(0, percentage));
			return (clampedValue / 100) * this.duration;
		},
		handlePercentageSeek(value) {
			this.highlightedTags = [];
			const time = this.percentageToTime(value);

			if (playerBus && playerBus.player) {
				if (this.isLive && time > playerBus.streamDuration) return;
				playerBus.seek(time);
			}

			this.$emit('seek', time);
		},
		handleTagHover(event, tag) {
			if (this.isTouchDevice && event.type === 'mouseenter') return;

			this.previewTriggerTarget = event.target;
			this.previewEvent = event;
			this.multiPreviewTags = [];
			this.multiPreviewGroup = null;
			this.singlePreviewTag = { ...tag };
		},
		handleTagLeave(event) {
			if (this.isTouchDevice && event?.type === 'mouseleave') return;

			if (event.relatedTarget !== this.$refs?.preview?.$el) {
				this.singlePreviewTag = null;
				this.multiPreviewTags = [];
				this.multiPreviewGroup = null;
			}
		},
		handlePillEnter({ event, group }) {
			if (this.isTouchDevice && event?.type === 'mouseenter') return;
			this.previewEvent = event;
			this.previewTriggerTarget = event.target;
			this.singlePreviewTag = null;
			this.multiPreviewGroup = group;
			const tags = this.tags.filter((tag) => group.ids.includes(tag.id));
			tags.sort((a, b) => a.time - b.time);
			this.multiPreviewTags = tags;
		},
		handlePillLeave({ event }) {
			if (this.isTouchDevice && event?.type === 'mouseleave') return;
			this.handleTagLeave(event);
		},
		handlePreviewEnter() {},
		handlePreviewLeave(event) {
			if (event.relatedTarget !== this.previewTriggerTarget) {
				this.dismissPreview();
			}
		},
		handlePreviewPlay(id) {
			const { tag } = this.tags.find((t) => t.id === id) || {};
			if (tag) {
				playerBus.seekTag(tag);
				playerBus.play();
			}
		},
		handlePreviewEdit(id) {
			const tag = this.tags.find((t) => t.id === id);
			this.$emit('request-tag-edit', tag);
			this.dismissPreview();
		},
		async handlePreviewRemove(id) {
			const tag = this.tags.find((t) => t.id === id);
			const eventTag = tag.tag;
			try {
				await this.$confirmDelete({
					title: this.$I18n.trans('commons.type_delete', {
						type: this.$I18n.trans_choice('one.tag', 1),
					}),
					body:   this.$I18n.trans('commons.delete_confirmation'),
					target: this.transTagLabel(tag.name, this.sportName, eventTag.tag?.type),
				});
				await this.deleteEventTags([tag]);
			} catch {}
		},
		handlePreviewShare(id) {
			const tag = this.tags.find((t) => t.id === id);
			bus.publish(EVENT.SHARE_TAG, tag.tag);
			this.dismissPreview();
		},
		handleCouch() {
			this.enableCouchMode();
		},
		dismissPreview() {
			this.singlePreviewTag = null;
			this.multiPreviewTags = [];
			this.multiPreviewGroup = null;
		},
		handleMainMouseMove(event) {
			// update hover cursor
			this.lastMainMoveEvent = event;
			const ref = this.$refs.main;
			if (!event || !ref) return;
			const rect = ref.getBoundingClientRect();
			const clientX = (event.touches ? event.touches[0].clientX : event.clientX);
			const x = clientX - rect.x;

			this.$raf('update-cursor-style', () => {
				this.mouseCursorStyle = {
					transform: `translateX(${x}px)`,

					...(this.isPreviewing ? { display: 'none' } : {}),
				};
			});
		},
		handleMarkingUpdate({ duration: markDuration, time }) {
			// when visible duration exceeds MARK_ZOOM_UPGRADE_TRIGGER_PERCENTAGE:
			// - adjust zoom so that the visible duration is increased by MARK_ZOOM_UPGRADE_RATIO
			// - adjust zoom start so that the edit bar starts at MARK_ZOOM_OFFSET_PERCENTAGE of the visible timeline

			const visibleDuration = this.duration / this.zoomLevel;
			const visibleMarkPercentage = (markDuration * 100) / visibleDuration;

			if (visibleMarkPercentage > MARK_ZOOM_UPGRADE_TRIGGER_PERCENTAGE) {
				const targetVisibleDuration = visibleDuration * MARK_ZOOM_UPGRADE_RATIO;
				const offsetDuration = (targetVisibleDuration * MARK_ZOOM_OFFSET_PERCENTAGE) / 100;

				this.zoomLevel = this.duration / targetVisibleDuration;
				this.zoomStart = Math.max(0, this.markingStart - offsetDuration);
			}

			// If time is provided, emit a seek event to the parent component
			if (time !== undefined) {
				this.$emit('seek', time);
			}
		},
		handlePlayerPause() {
			this.playedPreviewTag = null;
		},
		handleTagPlay(tag) {
			this.playedPreviewTag = tag;
		},
		handleAdModeEnter() {
			this.playerIsAdMode = true;
		},
		handleAdModeExit() {
			this.playerIsAdMode = false;

			if (this.autostartCouchMode) {
				this.disableAutostartCouchMode();
				this.handleCouch();
			}
		},
		handleAdTime({ position, duration }) {
			this.adPosition = position;
			this.adDuration = duration;
		},
		handleCursorDragStart() {
			if (this.cursorDraggingTimer) return;
			this.cursorDraggingTimer = setTimeout(() => {
				this.isCursorDragging = true;
				this.$emit('cursor-drag-start');
			}, 150);
		},
		handleCursorDragEnd() {
			if (this.isCursorDragging) {
				this.$emit('cursor-drag-end');
			}
			if (this.cursorDraggingTimer) {
				clearTimeout(this.cursorDraggingTimer);
				this.cursorDraggingTimer = null;
			}
			this.isCursorDragging = false;
		},
		forceRerender() {
			this.$nextTick(() => {
				this.$raf('handle-zoom-level-update', () => {
					this._computedWatchers.timelineItems.run();
					this._computedWatchers.timelineSingleItems.run();
					this._computedWatchers.timelineMultiItems.run();
					this._computedWatchers.needleStyle.run();
					this.$forceUpdate();
					this.updateNeedlePosition();
				});
			});
		},
		handleTagClick(_, tag) {
			this.handlePreviewPlay(tag.id);
		},
		handlePillClick(_, group) {
			const tag = group.tags[0].original;
			this.handlePreviewPlay(tag.id);
		},
		computePreviewTagName(tag) {
			const originalTag = tag.tag;
			if (originalTag.name === TAG_NAME_RUN && originalTag.player) {
				return `${originalTag.player.firstName} ${originalTag.player.lastName}`;
			}
			return this.translateTagName(tag.name, originalTag.tag?.type);
		},
		adaptToEditTagMode() {
			if (!this.editedTag || this.editedTag.isOutsideVideo) return;

			// change zoom level so that duration is 50%
			const tagDuration = this.editedTag.secondsBefore + this.editedTag.secondsAfter;
			const zoomSeconds = Math.max(Math.ceil((tagDuration / 5) * 10), 1); // avoid 0 duration tags
			this.zoomLevel = Math.max(1, this.duration / zoomSeconds);

			// change zoom start
			// const midpoint = this.editedTag.time - zoomSeconds / 2 + tagDuration / 2;
			this.zoomStart = Math.max(0, this.editedTag.time - this.editedTag.secondsBefore - zoomSeconds / 10);
		},
	},
};
</script>

<style lang="scss" scoped>
@import './timeline.scss';
@import '@shared/sass/shared-variables';

$tag-row-height: 30px;
$tag-row-gap: 5px;
$needle-pin-overflow-offset: 5px;

.timeline {
	position: relative;
	width: 100%;
	color: $timeline-foreground;
	cursor: pointer;
	user-select: none;
}

.timeline__inner-w {
	position: relative;
	display: grid;
	grid-template-columns: [center] 1fr;
	grid-template-rows: [top] $timeline-ruler-height [bottom] auto;
	grid-column-gap: 16px;
	grid-row-gap: 0;
	justify-items: stretch;
	align-items: stretch;
	width: 100%;

	.timeline.is-arena & {
		grid-column-gap: 24px;
	}

	// COLUMNS
	.timeline.has-couch-mode-button:not(.show-participants) & {
		grid-template-columns: [center] 1fr [right] auto;
	}

	.timeline.show-participants:not(.has-couch-mode-button) & {
		grid-template-columns: [left] auto [center] 1fr;
	}

	.timeline.has-couch-mode-button.show-participants & {
		grid-template-columns: [left] auto [center] 1fr [right] auto;
	}

	// ROWS
	.timeline.is-tagging-tool & {
		grid-template-rows: [top] $timeline-ruler-height [bottom] 96px;
	}

	.timeline.is-arena & {
		grid-template-rows: [bottom] auto;
	}

	.timeline.is-arena.show-participants & {
		grid-template-rows: [bottom] 80px;
	}
}

.timeline__top-left-corner {
	position: relative;

	.timeline.is-tagging-tool & {
		background-color: var(--color-grey-900);
	}
}

.timeline__needle {
	position: absolute;
	top: -24px;
	width: 1px;
	height: calc(100% + 24px);
	z-index: 10;
	background-color: $timeline-progress-needle-color;
	pointer-events: none;

	&::after {
		content: '';
		position: absolute;
		top: 0;
		left: 50%;
		width: 9px;
		height: 20px;
		display: block;
		transform: translate(-50%, 0);
		background-color: $timeline-strike-color;
		border-radius: 4px;
		z-index: 1;
		cursor: pointer;
	}

	&.-invisible {
		background: transparent;
	}

	&.is-live {
		background-color: $timeline-progress-needle-color-live;

		&::after {
			background-color: $timeline-strike-color-live;
		}
	}
}

.timeline__ruler {
	grid-row: top;
	grid-column: center;
	color: $timeline-ruler-label-foreground;
	font-size: $timeline-ruler-font-size;
	clip-path: inset(-100px 0px 0px -15px);
}

.timeline__participants {
	grid-row: bottom;
	grid-column: left;
	color: $timeline-participants-foreground;

	.timeline.is-desktop.is-arena &,
	.timeline.is-full-page.is-arena & {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		align-items: center;
	}

	.timeline.is-tagging-tool & {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		align-items: center;
		gap: $tag-row-gap;
		padding-top: 2px;
		padding-bottom: 2px;
		background-color: var(--color-grey-900);
		z-index: 2;
	}

	.timeline.is-edit-mode & {
		justify-content: center;
	}
}

.timeline__participant {
	display: flex;
	align-items: center;
	justify-content: center;

	.timeline.is-desktop.is-arena &,
	.timeline.is-full-page.is-arena & {
		height: auto;
	}

	.timeline.is-arena.is-desktop &,
	.timeline.is-full-page.is-desktop & {

		width: 76px;
		height: 75px;

		body.is-page-embed-player & {
			height: 50%;
		}

		&:not(:first-child) {
			margin-top: 10px;
		}
	}

	&.-multi-spacer {
		display: none;
	}
}

.timeline__participant-image {
	width: 32px;
	height: 32px;
}

.timeline__main-time-axis {
	position: relative;
	grid-row: bottom;
	grid-column: center;
	width: 100%;
	height: 100%;
	min-height: 30px;

	.timeline.is-tagging-tool & {
		&::before,
		&::after {
			content: '';
			position: absolute;
			top: 0;
			left: -#{$needle-pin-overflow-offset};
			background-color: $timeline-background;
			height: 100%;
			width: 5px;
			z-index: 2;
			clip-path: border-box;
		}

		&::after {
			right: -#{$needle-pin-overflow-offset};
			left: auto;
		}
	}

}

.timeline__main-time-axis-clipper {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;

	&.-timebox {
		clip-path: inset(-100px -25px 0px -25px);
	}

	&.-main {
		clip-path: inset(-200px -#{$needle-pin-overflow-offset} 0px -#{$needle-pin-overflow-offset});
	}
}

.timeline__click-w {
	position: absolute;
	top: 0;
	left: $timeline-horizontal-margin;
	height: 100%;
	min-width: calc(100% - #{$timeline-horizontal-margin});
}

.timeline__click-w-inner {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
}

.timeline__progress-bar {
	position: absolute;
	top: 50%;
	left: 0;
	height: 1px;

	width: $timeline-horizontal-margin;
	background-color: $timeline-strike-color;

	.timeline__main-time-axis.is-live & {
		background-color: $timeline-strike-color-live;
	}

	.timeline.is-arena & {
		background-color: $timeline-viewmode-strike-color;
		height: $timeline-viewmode-strike-height;
		transform: translateY(-50%);
		z-index: 2;
	}

	.timeline.is-arena.is-vod &,
	.timeline.is-full-page.is-arena.is-vod & {
		background-color: $timeline-viewmode-vod-strike-color;
	}

	.timeline.is-live & {
		background-color: $timeline-strike-color-live;
	}
}

.timeline__needle-to-live-strike {
	position: absolute;
	top: 50%;
	left: 0;

	.timeline.is-arena & {
		background-color: var(--color-grey-300);
		height: $timeline-viewmode-strike-height;
		transform: translateY(-50%);
	}
}

.timeline__tag-bg-rows {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	gap: $tag-row-gap;

	// &.is-duell {}
}

.timeline__tag-bg-row {
	position: relative;
	width: 100%;
	flex-grow: 1;
	background-color: var(--color-grey-850);
	border-radius: $border-radius--sm;

	.timeline__tag-bg-rows.is-live & {
		&::after {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			width: var(--live-progress-width);
			height: 100%;
			background-color: $timeline-live-progress-covered-color;
		}
	}
}

.timeline__tag {
	border-radius: 100%;
	position: absolute;
	cursor: pointer;
	background-color: var(--color-white);
	transform: translate(-50%, 50%);
	z-index: 3;

	&.-light-mode {
		background-color: var(--color-grey-500);
	}

	&.-pill {
		transform: translate(0, 50%);
	}

	&.-neutral:not(.is-active) {
		background-color: var(--color-grey-600);
	}

	&.is-active {
		background-color: var(--color-hype-yellow);

		::v-deep {
			.fill {
				background-color: var(--color-hype-yellow);
			}
		}
	}

	.timeline.is-tagging-tool & {
		&.-score-1 { background: rgb(255, 255, 153); } /* Light Yellow */
		&.-score-2 { background: rgb(255, 230, 128); }
		&.-score-3 { background: rgb(255, 204, 102); }
		&.-score-4 { background: rgb(255, 179, 77); }
		&.-score-5 { background: rgb(255, 153, 51); }
		&.-score-6 { background: rgb(255, 128, 26); }
		&.-score-7 { background: rgb(255, 102, 0); }
		&.-score-8 { background: rgb(230, 77, 0); }
		&.-score-9 { background: rgb(204, 51, 0); } /* Deep Red */
		&.-score-10 { background: rgb(179, 0, 0); } /* Darkest Red */
	}
}

.timeline__timebox {
	position: absolute;
	top: calc(0% - 60px);
	background-color: var(--color-white);
	color: var(--color-shadow-grey);
	padding: 0px 4px;
	border-radius: 2px;
	font-size: 12px;
	opacity: 0;
	transition: opacity 0.25s linear;
	pointer-events: none;

	.timeline:not(.is-zoom-changing):hover & {
		opacity: 1;
	}
}

.timeline__multi-preview {
	z-index: 999;
}

.timeline__seek-bar {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	background-color: var(--color-grey-300);
	height: $timeline-viewmode-strike-height;
	width: calc(100% - #{$timeline-horizontal-margin});
	margin-left: $timeline-horizontal-margin;
	border-radius: 0 4px 4px 0;
	opacity: 0.4;
}

.timeline__after {
	position: relative;
	grid-row: bottom;
	grid-column: right;

	.timeline.is-arena & {
		display: grid;
		place-items: center;
	}
}

.timeline__couch-button {
	color: var(--color-soft-white);

	&.-light-mode {
		color: var(--color-black);
	}
}

</style>
